// Single source of sample content for the Breakage Board design prototype. Board clock ≈ Thursday 11:20 AM.
import { isBoardFresh } from "@/lib/freshness";

export const BOARD_NOW = "11:20 AM";
// Placeholder until the owner confirms the limit.
export const FRESHNESS_LIMIT_MINUTES = 30;

export const WATCH_STATES = ["healthy", "broken", "alert", "waiting", "stale", "empty"];

export const ALERT_LINE = "Alert fired — condition met. The watch is doing its job.";
const EMPTY_LINE = "No activity yet. The first check lands within a day.";
const STALE_SINCE = "Tue 6:00 AM";

// pastRuns: the 15 runs before the newest, oldest first. "." clean, "r" past failed, "a" past flagged.
export const WATCHES = [
  {
    slug: "inbox-scan",
    name: "Inbox scan",
    brand: "TrustOffice",
    does: "Every hour it reads the new email coming into TrustOffice and sorts each message so nothing sits unseen.",
    why: "If the scan stops, new client emails pile up quietly and nobody notices until someone follows up annoyed.",
    pastRuns: ".......r.......",
    healthy: { line: "All 63 new emails read and sorted today.", number: "63", label: "emails sorted today", stamp: "11:00 AM", lastFailed: "September 30" },
    broken: {
      time: "10:14 AM",
      purpose: "every new email gets read and sorted",
      number: "2",
      label: "scans missed today",
      kit: "Kit found the mailbox sign-in expired overnight. He's renewing it and will re-run the scan. Expected back to green by 1:00 PM.",
    },
    alert: { number: "41", label: "emails unsorted over an hour", stamp: "11:00 AM", kit: "Kit is checking why sorting slowed down this morning. Expect an update by 12:00 PM." },
    waiting: {
      decision: "choose which folder Jake's new client emails should go to.",
      number: "1",
      label: "folder needs your decision",
      stamp: "10:00 AM",
      kit: "Nothing Kit can do without you — it needs your decision on Jake's folder.",
    },
  },
  {
    slug: "seo-audit",
    name: "SEO audit",
    brand: "TrustMinutes",
    does: "Every morning it walks through all 48 pages of the TrustMinutes site and checks each one still loads, keeps its title and can be found in search.",
    why: "A page that quietly drops out of search costs visitors for weeks before anyone spots it.",
    pastRuns: ".............a.",
    healthy: { line: "All 48 page checks passed.", number: "48", label: "pages passing", stamp: "6:00 AM", lastFailed: "September 22" },
    broken: {
      time: "6:00 AM",
      purpose: "all 48 pages still load and can be found in search",
      number: "3",
      label: "pages failing",
      kit: "Investigating now — expect an update by 12:30 PM.",
    },
    alert: { number: "5", label: "pages missing a title", stamp: "6:00 AM", kit: "Kit is checking which recent edit removed the page titles. Expect an update by 12:30 PM." },
    waiting: {
      decision: "approve the new page titles Jake drafted.",
      number: "6",
      label: "titles need your decision",
      stamp: "6:00 AM",
      kit: "Nothing Kit can do without you — it needs your decision on Jake's page titles.",
    },
  },
  {
    slug: "nightly-backup",
    name: "Nightly backup",
    brand: "Portfolio",
    does: "Every night it copies all seven portfolio sites, then opens each copy to make sure it actually restores.",
    why: "A backup that can't be opened is no backup at all — you only find out on the worst day.",
    pastRuns: "...............",
    healthy: { line: "Last night's backup finished and was verified.", number: "7", label: "sites backed up", stamp: "2:00 AM", lastFailed: "August 28" },
    broken: {
      time: "2:00 AM",
      purpose: "every site was copied and each copy opens",
      number: "2",
      label: "sites not backed up",
      kit: "Investigating now — expect an update by 12:30 PM.",
    },
    alert: { number: "1", label: "site copy smaller than usual", stamp: "2:00 AM", kit: "Kit is checking whether pages were removed from that site. Expect an update by 12:30 PM." },
    waiting: {
      decision: "say whether the retired gallery site still needs backing up.",
      number: "1",
      label: "site needs your decision",
      stamp: "2:00 AM",
      kit: "Nothing Kit can do without you — it needs your decision on the gallery site.",
    },
  },
  {
    slug: "payment-reconciliation",
    name: "Payment reconciliation",
    brand: "WingPoint",
    does: "Each morning it matches every WingPoint booking payment against what actually landed in the bank.",
    why: "Missed refunds and double charges are easy to overlook and hard to explain to a customer later.",
    pastRuns: "...............",
    healthy: { line: "All 112 payments matched the bank.", number: "112", label: "payments matched", stamp: "9:30 AM", lastFailed: "September 9" },
    broken: {
      time: "9:30 AM",
      purpose: "every booking payment matches what reached the bank",
      number: "4",
      label: "payments not matched",
      kit: "Kit found the bank statement arrived in a new layout. He's teaching the check to read it and will re-run it. Expected back to green by 1:30 PM.",
    },
    alert: { number: "3", label: "customers charged twice", stamp: "9:30 AM", kit: "Kit is checking whether the booking form charged three customers twice. Expect an update by 12:00 PM." },
    waiting: {
      decision: "approve the refund for Dory's cancelled booking.",
      number: "1",
      label: "payment needs your decision",
      stamp: "9:30 AM",
      kit: "Nothing Kit can do without you — it needs your decision on Dory's refund.",
    },
  },
  {
    slug: "city-page-build",
    name: "City page build",
    brand: "True Joy Birthing",
    does: "Each morning it rebuilds the True Joy Birthing city pages and checks every one is live and showing the right details.",
    why: "A city page that goes missing means families searching in that city simply never find us.",
    pastRuns: "...............",
    healthy: { line: "All 24 city pages built and live.", number: "24", label: "city pages live", stamp: "8:45 AM", lastFailed: "September 15" },
    broken: {
      time: "8:45 AM",
      purpose: "every city page is built and live",
      number: "3",
      label: "city pages down",
      kit: "Investigating now — expect an update by 1:00 PM.",
    },
    alert: { number: "2", label: "pages showing old prices", stamp: "8:45 AM", kit: "Kit is checking where the old prices are coming from. Expect an update by 12:00 PM." },
    waiting: {
      decision: "approve the two new city pages before they go live.",
      number: "2",
      label: "pages need your decision",
      stamp: "8:45 AM",
      kit: "Nothing Kit can do without you — it needs your decision on the two new pages.",
    },
  },
  {
    slug: "lead-router",
    name: "Lead router",
    brand: "AeriusView",
    does: "Through the day it takes every new enquiry from the AeriusView site and hands it to the right person, usually Jake.",
    why: "A lead that isn't picked up within the hour usually goes somewhere else.",
    pastRuns: "..........a....",
    healthy: { line: "All 9 new leads reached the right person.", number: "9", label: "leads routed today", stamp: "11:05 AM", lastFailed: "September 26" },
    broken: {
      time: "11:05 AM",
      purpose: "every new enquiry reaches the right person",
      number: "3",
      label: "leads not routed",
      kit: "Kit found the hand-off to Jake's inbox stopped this morning. He's reconnecting it and will re-send the waiting leads. Expected back to green by 12:30 PM.",
    },
    alert: { number: "0", label: "leads routed since 8:00 AM", stamp: "11:05 AM", kit: "Kit is checking whether the site's contact form stopped sending. Expect an update by 12:00 PM." },
    waiting: {
      decision: "choose who covers new leads while Jake is away.",
      number: "1",
      label: "cover needs your decision",
      stamp: "11:05 AM",
      kit: "Nothing Kit can do without you — it needs your decision on who covers Jake's leads.",
    },
  },
];

const contentFor = (w, state) => {
  const about = { does: w.does, why: w.why };
  const { healthy: h, broken: b, alert: a, waiting: wt } = w;
  switch (state) {
    case "broken":
      return {
        line: `${w.name} — Failed last run ${b.time}. It checks ${b.purpose}. Kit is fixing this.`,
        number: b.number,
        label: b.label,
        stamp: `checked ${b.time}`,
        detail: { ...about, lastFailed: `Today, ${b.time}. Before that: ${h.lastFailed}.`, kit: b.kit },
      };
    case "alert":
      return {
        line: ALERT_LINE,
        number: a.number,
        label: a.label,
        stamp: `checked ${a.stamp}`,
        detail: { ...about, lastFailed: "It hasn't — this alert means the watch worked.", kit: a.kit },
      };
    case "waiting":
      return {
        line: `Waiting on you: ${wt.decision}`,
        number: wt.number,
        label: wt.label,
        stamp: `checked ${wt.stamp}`,
        detail: { ...about, lastFailed: `It hasn't failed — it paused at ${wt.stamp} for your decision.`, kit: wt.kit },
      };
    case "stale":
      return {
        line: `${w.name} hasn't run since ${STALE_SINCE} — the numbers below may be old.`,
        number: h.number,
        label: h.label,
        stamp: `checked ${STALE_SINCE}`,
        detail: { ...about, lastFailed: `Last ran ${STALE_SINCE}.`, kit: "Kit is checking why it stopped running. Expect an update by 12:30 PM." },
      };
    case "empty":
      return {
        line: EMPTY_LINE,
        number: "0",
        label: "checks so far",
        stamp: "not checked yet",
        detail: { ...about, lastFailed: "It hasn't run yet.", kit: "Kit is watching for the first check — it lands within a day." },
      };
    default:
      return {
        line: h.line,
        number: h.number,
        label: h.label,
        stamp: `checked ${h.stamp}`,
        detail: { ...about, lastFailed: `${h.lastFailed} — fixed same day.`, kit: "Nothing needed right now." },
      };
  }
};

const TRACK_LENGTH = 16;
const PAST = { ".": "clean", r: "past-failed", a: "past-flagged" };
const NEWEST = { healthy: "clean", broken: "failed", alert: "flagged", waiting: "flagged" };

// 16 runs, oldest first. Newest = current state. Stale: the last 4 slots didn't run. Empty: nothing ran.
export const buildTrack = (pastRuns, state) => {
  if (state === "empty") return Array(TRACK_LENGTH).fill("missed");
  const past = [...pastRuns].map((c) => PAST[c]);
  if (state === "stale") return [...past.slice(0, TRACK_LENGTH - 4), ...Array(4).fill("missed")];
  return [...past, NEWEST[state]];
};

export const SCENARIOS = {
  typical: {
    asOf: "11:20 AM",
    label: "Typical day",
    states: {
      "inbox-scan": "broken",
      "seo-audit": "healthy",
      "nightly-backup": "healthy",
      "payment-reconciliation": "waiting",
      "city-page-build": "healthy",
      "lead-router": "alert",
    },
  },
  demo: {
    asOf: "11:20 AM",
    label: "All states",
    states: {
      "inbox-scan": "broken",
      "seo-audit": "stale",
      "nightly-backup": "healthy",
      "payment-reconciliation": "waiting",
      "city-page-build": "empty",
      "lead-router": "alert",
    },
  },
  quiet: {
    asOf: "11:20 AM",
    label: "Quiet day",
    states: Object.fromEntries(WATCHES.map((w) => [w.slug, "healthy"])),
  },
};

export const buildTiles = (states) =>
  WATCHES.map((w) => {
    const state = states[w.slug] || "healthy";
    return { slug: w.slug, name: w.name, brand: w.brand, state, track: buildTrack(w.pastRuns, state), ...contentFor(w, state) };
  });

export const buildClock = (asOf) => {
  const fresh = isBoardFresh(asOf, BOARD_NOW, FRESHNESS_LIMIT_MINUTES);
  return { asOf, fresh, dead: false, stampText: asOf };
};
